import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../DesignSystem/Sidebar";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import CustomSelect from "../../DesignSystem/Dropdowns/CustomSelect";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../DesignSystem/Buttons/SecondaryButton";
import {
  PostCard,
  Row,
} from "../../Adviser/Tabs/HelperFunctions/HelperFunctions";
import { useUserEmail } from "../../SignIn/UserEmailContext";
import { useUserPermissions } from "../../SignIn/UserPermissionsContext";
import { PLAN_CATEGORIES } from "./utils/planTypeMappingConstants";
import {
  getPlanTypeMapping,
  updatePlanTypeCategory,
} from "./utils/planTypeMappingService";
import "../../../css/Adviser/AdviserDetailsPage.css";
import "../../../css/Adviser/EditAdviserDetailsPage.css";

const CATEGORY_OPTIONS = PLAN_CATEGORIES.map((category) => ({
  id: category,
  name: category,
}));

const EditPlanTypeMappingPage = () => {
  const { planTypeId = "" } = useParams();
  const navigate = useNavigate();
  const { userEmail } = useUserEmail();
  const { userPermissions = [] } = useUserPermissions();
  const canManage = ["Administrator", "IO Admin"].some((permission) =>
    userPermissions.includes(permission),
  );

  const decodedId = useMemo(() => decodeURIComponent(planTypeId), [planTypeId]);
  const [mapping, setMapping] = useState(null);
  const [planCategory, setPlanCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!canManage || !decodedId) return;

    setLoading(true);
    getPlanTypeMapping(decodedId)
      .then((result) => {
        setMapping(result);
        setPlanCategory(result.planCategory || "");
      })
      .catch((loadError) => {
        setError(loadError.message || "Could not load the plan type mapping.");
      })
      .finally(() => setLoading(false));
  }, [canManage, decodedId]);

  if (!canManage) return <Navigate to="/Department/Compliance" replace />;

  const handleSave = async () => {
    if (!mapping || !planCategory) return;
    setSaving(true);
    setError("");
    try {
      await updatePlanTypeCategory({
        mapping,
        planCategory,
        actor: userEmail,
      });
      navigate("/compliance/plan-type-mapping");
    } catch (saveError) {
      // setError(saveError.message || "Could not update the Rosemount Category.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="main-content">
      <Helmet>
        <title>Edit Plan Type Mapping</title>
      </Helmet>
      <Sidebar />
      <main className="Main-Container">
        <div className="smFeed">
          <Breadcrumb
            items={[
              { label: "Compliance", to: "/Department/Compliance" },
              {
                label: "Plan Type Mapping",
                to: "/compliance/plan-type-mapping",
              },
              { label: "Edit" },
            ]}
          />

          <div className="smEditActions">
            <div className="smEditActionsLeft">
              <h3 className="FP_H3">
                {mapping?.planTypeName || "Edit Plan Type Mapping"}
              </h3>
            </div>
            <div className="smEditActionsLeft">
              {/* <SecondaryButton
                label="Cancel"
                disabled={saving}
                onClick={() => navigate("/compliance/plan-type-mapping")}
              /> */}
              <PrimaryButton
                label={saving ? "Saving..." : "Save Details"}
                disabled={
                  saving ||
                  loading ||
                  !planCategory ||
                  planCategory === mapping?.planCategory
                }
                onClick={handleSave}
              />
            </div>
          </div>

          {error && (
            <div className="adviserLicenseError Content-Small">{error}</div>
          )}

          {loading ? (
            <p className="Content-Small muted">Loading mapping...</p>
          ) : mapping ? (
            <div className="smRatesEditLayout">
              <PostCard title="Rosemount Category">
                <CustomSelect
                  label="Rosemount Category"
                  options={CATEGORY_OPTIONS}
                  value={planCategory}
                  onChange={(event) => setPlanCategory(event.target.value)}
                  placeholder="Select Rosemount category"
                  clearable={false}
                  searchable={false}
                  required
                />
              </PostCard>

              <PostCard title="Plan Type Details">
                <div className="smRatesAdviserDetails">
                  <Row label="Plan Type ID" value={mapping.planTypeId} />
                  <Row label="Plan Type Name" value={mapping.planTypeName} />
                  <Row
                    label="Portfolio Category"
                    value={mapping.portfolioCategory}
                  />
                  <Row label="Product Group" value={mapping.productGroup} />
                </div>
              </PostCard>
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
};

export default EditPlanTypeMappingPage;
